import {AxiosProgressEvent, CancelToken} from 'axios';
import parseFileName from 'common/src/utils/fileUtils/parseFileName';

import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {handleApiError} from '../errorHandling/handleApiError';
import {fileUploadApi} from '../fileUpload/fileUpload.api';
import {JobExecutionParams} from '../jobs/jobs.types';
import waitForJob from '../jobs/utils/waitForJob';
import {toRemoteMappingRule} from '../locationFeed/locationFeed.dataConverter';
import {FeedUploadMethod} from '../locationFeed/locationFeed.enums';
import {FeedMappingDictionary, FeedMappingRule} from '../locationFeed/locationFeed.types';
import getFeedRuleKey from '../locationFeed/utils/getFeedRuleKey';
import getFeedRuleValue from '../locationFeed/utils/getFeedRuleValue';
import {MacroItem} from '../macros/macros.types';
import {ProductType, ProductTypeName} from '../products/products.enums';
import taxonomyApi from '../taxonomy/taxonomy.api';
import {TaxonomyCategory, TaxonomyEntry, TaxonomyType} from '../taxonomy/taxonomy.types';
import {toClientFeed} from './feeds.dataConverter';
import {
  ColumnsMapping,
  CreateFeedPayload,
  Feed,
  FeedAdvancedParams,
  FeedAdvancedPayload,
  FeedEnrichmentJob,
  FeedFileMetadata,
  GoogleSheetDetails,
  Mapping,
  OlympicEventsConfig,
  OlympicMedalsConfig,
  RemoteFeed,
  SportsConfig,
  UpdateFeedPayload,
} from './feeds.types';
import {taxonomyDataTypeLabels} from '../taxonomy/taxonomy.config';

const basePath = 'Feeds';

async function getAllNew(): Promise<Feed[]> {
  return (await apiV2Service
    .executeGetRequest<RemoteFeed[]>(`${basePath}/GetAll`)
    .then(({data}) => data.map(toClientFeed))
    .catch(handleApiError)) as Feed[];
}

async function get(id: number): Promise<Feed> {
  const {data: feed} = await apiV2Service.executeGetRequest<RemoteFeed>(`${basePath}/${id}`);

  return toClientFeed(feed);
}

async function updateFeed({
  feedId,
  updatePayload,
}: {
  feedId: number;
  updatePayload: UpdateFeedPayload;
}) {
  return apiV2Service.executePostRequest<void>(`${basePath}/${feedId}`, {}, {...updatePayload});
}

async function update(params: {
  id: number;
  name?: string;
  feedType?: number;
  feedUploadMethod?: FeedUploadMethod;
}) {
  return apiService.executePostRequest<void>(`${basePath}/Update`, {
    ...params,
  });
}

async function duplicateFeed(feedsIdsToDup: number) {
  return apiService.executePostRequest<void>(`${basePath}/DuplicateFeed`, {
    feedsIdsToDup,
  });
}

async function updateFeedColumnMapping({
  feedId,
  isEnrich,
  updatePayload,
}: {
  feedId: number;
  isEnrich: boolean;
  updatePayload: Partial<ColumnsMapping>;
}) {
  return apiService.executePostRequest<void>(
    `${basePath}/UpdateFeedMapping`,
    {
      feedId,
      isEnrich,
    },
    {
      Data: JSON.stringify(updatePayload),
    },
  );
}

async function updateFeedAdvancedConfiguration({
  feedId,
  updatePayload,
}: {
  feedId: number;
  updatePayload: FeedAdvancedPayload;
}) {
  return apiService.executePostRequest<void>(
    `${basePath}/UpdateFeedAdvancedConfiguration`,
    {
      feedId,
    },
    {
      Data: JSON.stringify(updatePayload),
    },
  );
}

async function feedFileMetadataUpdate(params: {
  feedId: number;
  isEnrich?: boolean;
  fileUrl?: string;
}) {
  return apiService.executePostRequest<void>(`${basePath}/StartFeedFileMetadataUpdate`, {
    ...params,
  });
}

async function getFeedFileMetadata(params: {
  feedId: number;
  isEnrich?: boolean;
}): Promise<FeedFileMetadata> {
  const {
    data: [fileMetadata],
  } = await apiService.executeGetRequest<FeedFileMetadata[]>(`${basePath}/GetFeedFileMetadata`, {
    ...params,
  });

  return fileMetadata;
}

async function getFeedEnrichmentJob(feedId: number): Promise<FeedEnrichmentJob> {
  const {
    data: [feedEnrichmentJob],
  } = await apiService.executeGetRequest<FeedEnrichmentJob[]>(`${basePath}/GetFeedEnrichmentJob`, {
    feedId,
  });

  return feedEnrichmentJob;
}

async function deleteFeed(id: number) {
  return apiV2Service.executeDeleteRequest<void>(`${basePath}/Delete/${id}`);
}

async function createFeed(params: CreateFeedPayload): Promise<number> {
  const {data: feedId} = await apiV2Service.executePostRequest<number>(
    `${basePath}/Add`,
    {},
    {...params},
  );

  return feedId;
}

async function getMappingDictionary(params: {
  productType: ProductTypeName | ProductType;
}): Promise<TaxonomyEntry[]> {
  const {
    data: [dictionary],
  } = await apiService.executeGetRequest<[FeedMappingDictionary]>(
    `${basePath}/GetFullFeedMappingDic`,
    {
      ...params,
    },
  );

  return Object.values(dictionary).map(({shortName, fullName, isRequired}) => ({
    value: shortName,
    label: fullName,
    description: null,
    isRequired,
    defaultUIName: fullName,
  }));
}

async function getFeedEnrichmentMappingDic(params: {
  productType: ProductTypeName;
}): Promise<TaxonomyEntry[]> {
  const {
    data: [dictionary],
  } = await apiService.executeGetRequest<[FeedMappingDictionary]>(
    `${basePath}/GetFeedEnrichmentMappingDic`,
    {
      ...params,
    },
  );

  return Object.values(dictionary).map(({shortName, fullName, isRequired, taxonomyKey}) => ({
    value: shortName,
    label: fullName,
    description: null,
    isRequired,
    defaultUIName: fullName,
    taxonomyKey,
  }));
}

async function updateFeedMappingRules({feedId, rules}: {feedId: number; rules: FeedMappingRule[]}) {
  const updatePayload = rules
    .filter((rule) => !!rule.columnName)
    .map((rule) => ({
      ...rule,
      capitalization: 0,
      defaultValue: '',
      overrideValue: '',
      regexExtract: '',
      regexReplace: null,
      replace: null,
      removeBackground: false,
      [getFeedRuleKey(rule.ruleType)]: getFeedRuleValue(rule.ruleType, rule),
    }))
    .map(toRemoteMappingRule);

  return apiService.executePostRequest<void>(
    `${basePath}/UpdateFeedMappingRules`,
    {
      feedId,
      isEnrich: false,
    },
    {
      Data: JSON.stringify(updatePayload),
    },
  );
}

async function updateFeedMappingAdvancedParameters({
  feedId,
  updatePayload,
}: {
  feedId: number;
  updatePayload: FeedAdvancedParams;
}) {
  return apiService.executePostRequest<void>(
    `${basePath}/UpdateFeedMappingAdvancedParameters`,
    {
      feedId,
    },
    {
      Data: JSON.stringify(updatePayload),
    },
  );
}

async function updateFeedMapping({
  feedId,
  updatePayload,
}: {
  feedId: number;
  updatePayload: Mapping;
}) {
  return apiService.executePostRequest<void>(
    `${basePath}/UpdateFeedMapping`,
    {
      feedId,
    },
    {
      Data: JSON.stringify(updatePayload),
    },
  );
}

async function updateFeedInventory(feedId: number) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(`${basePath}/${feedId}/updateInventory`)
    .then(({data: {executionId: jobId}}) => {
      waitForJob({
        jobId,
        jobName: `Update Feed F${feedId} Inventory Items`,
      });
    });
}

async function validateFeedItems(feedId: number) {
  return apiService.executePostRequest<void>(`FeedItems/ValidateFeedItems`, {
    feedId,
  });
}

async function updateFileSourceConfig({
  feedUploadMethod,
  fileUrl,
  cronExpression,
  feedId,
  timeZone,
  customParams,
  sports,
  olympicMedals,
  olympicEvents,
  googleSheetDetails,
}: {
  feedUploadMethod: FeedUploadMethod;
  fileUrl: string;
  feedId: number;
  timeZone?: string;
  cronExpression?: string;
  customParams?: Record<string, string>;
  sports?: SportsConfig;
  olympicMedals?: OlympicMedalsConfig;
  olympicEvents?: OlympicEventsConfig;
  googleSheetDetails?: GoogleSheetDetails;
}) {
  return apiService.executePostRequest<unknown>(
    `${basePath}/UpdateFileSourceConfig`,
    {
      feedUploadMethod,
      fileUrl,
      feedId,
      ...(timeZone && {timeZone}),
      ...(cronExpression && {cronExpression}),
    },
    {
      Data: JSON.stringify({
        customParams,
        sports,
        googleSheetDetails,
        olympicMedals,
        olympicEvents,
      }),
    },
  );
}

async function uploadFileSource({
  file,
  feedId,
  cancelToken,
  onUploadProgress,
}: {
  file: File;
  feedId: number;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  cancelToken?: CancelToken;
}) {
  const urlObj = await fileUploadApi.getPresignedUrlForFeedFileUpload({
    feedId,
    contentType: file.type,
    fileExtension: parseFileName(file.name).fileExtension,
  });

  await fileUploadApi.uploadFileToS3({
    file,
    cancelToken,
    url: urlObj.presignedUrl,
    onUploadProgress,
    contentType: file.type,
  });

  return urlObj;
}

async function getCurrentFeedFile(feedId: number) {
  const {
    data: [fileUrl],
  } = await apiService.executeGetRequest<[string]>(`${basePath}/GetCurrentFeedFile`, {
    feedId,
  });

  return fileUrl;
}

function getSupportedSignals(feedId: number) {
  return apiV2Service
    .executeGetRequest<MacroItem[]>(`${basePath}/${feedId}/columnSignals`)
    .then((response) => response.data);
}

async function enqueueFeedEnrichmentJob(feedId: number) {
  const {data: executionId} = await apiV2Service.executePostRequest<number>(
    `${basePath}/${feedId}/EnqueueEnrichmentScheduledJob`,
  );

  return executionId;
}

async function getFieldsForMapping(category: TaxonomyCategory): Promise<TaxonomyEntry[]> {
  const fields = await taxonomyApi.getFields();
  const taxonomy = await taxonomyApi.get(
    category === TaxonomyType.Location ? TaxonomyType.Location : TaxonomyType.Product,
  );

  const fieldsForMapping = (fields[category] || []).map((field) => {
    const fieldTax = taxonomy.find(({key}) => key === field.key);

    if (!fieldTax) {
      return null;
    }

    return {
      value: field.key,
      label: fieldTax.uIName,
      description: fieldTax.description,
      isRequired: field.isRequired,
      defaultUIName: field.defaultUIName,
      isDeprecated: field.isDeprecated,
      //used to group mapping fields by taxonomy data type
      category: taxonomyDataTypeLabels[field.dataType],
    };
  });

  return fieldsForMapping.filter(Boolean) as TaxonomyEntry[];
}

export default {
  get,
  update,
  getAllNew,
  deleteFeed,
  getCurrentFeedFile,
  updateFileSourceConfig,
  uploadFileSource,
  createFeed,
  updateFeed,
  duplicateFeed,
  validateFeedItems,
  updateFeedInventory,
  updateFeedMappingRules,
  updateFeedColumnMapping,
  updateFeedMapping,
  getFeedFileMetadata,
  getFeedEnrichmentJob,
  getMappingDictionary,
  getFeedEnrichmentMappingDic,
  feedFileMetadataUpdate,
  updateFeedAdvancedConfiguration,
  updateFeedMappingAdvancedParameters,
  getSupportedSignals,
  enqueueFeedEnrichmentJob,
  getFieldsForMapping,
};
